/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import Joi from 'joi';
import { emailTypes, phoneNumberTypes } from './Helpers';
import validateEmailAddress from '../../../../../../../helpers/validateEmailAddress'

export const schemaForAddNewRecipient = Joi.object({
    rcpFirstName: Joi.string()
        .trim()
        .min(1)
        .max(14)
        .required()
        .pattern(/^[a-zA-Z ]+$/)
        .messages({
            'string.empty': 'First Name is required.',
            'string.pattern.base': 'Please use only alphabetic characters.',
        })
        .label('First Name'),

    rcpLastName: Joi.string()
        .trim()
        .min(1)
        .max(14)
        .required()
        .pattern(/^[a-zA-Z ]+$/)
        .messages({
            'string.empty': 'Last Name is required.',
            'string.pattern.base': 'Please use only alphabetic characters.',
        })
        .label('Last Name'),
    rcpEmployerName: Joi.string()
        .min(0)
        .max(200)
        .optional()
        .label('Company Name'),
    relationship: Joi.any().optional().label('Relationship'),
    occasions: Joi.array().items(
        Joi.object().keys({
            dateMM: Joi.string().allow(''),
            dateDD: Joi.string().allow(''),
            dateYYYY: Joi.any().optional(),
            desc: Joi.string().optional(),
            occasion: Joi.string().optional(),
            occasionId: Joi.any().optional(),
            subscribed: Joi.boolean().optional(),
            subscribedEmail: Joi.boolean().optional(),
            subscribedPhone: Joi.boolean().optional(),
            reminderEmail: Joi.any().optional(),
            reminderPhone: Joi.string()
                .trim()
                .when('subscribedPhone', {
                    is: true,
                    then: Joi.string()
                        .trim()
                        .pattern(/^\(?([1-9]\d{2})\)? \d{3}-\d{4}$/)
                        .min(14)
                        .max(15)
                        .required()
                        .messages({
                            'string.empty': 'Mobile number is required.',
                            'string.min': 'Mobile number must be 10 digits long.',
                            'string.max': 'Mobile number must be 10 digits long.',
                            'string.pattern.base': 'Please enter a valid mobile number.',
                        })
                        .label('Mobile number'),
                    otherwise: Joi.string().allow(''),
                }
                ),
            isOneDay: Joi.any().optional(),
            isThreeDays: Joi.any().optional(),
            isOneWeek: Joi.any().optional(),
            isTwoWeek: Joi.any().optional(),
            isOneMonth: Joi.any().optional(),
            isSnoozed: Joi.any().optional(),
        })
            .custom((value, helpers) => {
                // Check if at least one of dateMM or dateDD has a value
                if (!value.dateMM && value.dateDD) {
                    return helpers.error('any.required', {
                        custom: {
                            message: 'Month is required.',
                        },
                    });
                }
                if (value.dateMM && !value.dateDD) {
                    return helpers.error('any.required', {
                        custom: {
                            message: 'Day is required',
                        },
                    });
                }

                return value;

            }),
    ).messages({
        'any.required': '{{#custom.message}}',
    }),
    addressType: Joi.string()
        .custom((value, helpers) => {
            const [{ addressType, addressLineOne, city, state, zip }] = helpers.state.ancestors
            if ((!addressType && (addressLineOne || state || zip || city))) {
                return helpers.error('string.required', {
                    custom: {
                        message: 'Location Type is required.',
                    },
                });
            }

            return value;
        })
        .optional()
        .min(0)
        .messages({
            'string.required': '{{#custom.message}}',
        }),
    employerName: Joi.string().custom((value, helpers) => {
        const [{ employerName }] = helpers.state.ancestors
        if (employerName.trim().length === 0) {
            return helpers.error('string.empty', {
                custom: {
                    message: 'Employer Name is required.',
                },
            });
        }

        return value;
    })
        .when('addressType', {
            is: 'Business',
            then: Joi.string().max(200).required(),
        })
        .when('addressType', {
            is: 'Hospital',
            then: Joi.string().max(200).required(),
        })
        .when('addressType', {
            is: 'School',
            then: Joi.string().max(200).required(),
        })
        .when('addressType', {
            is: 'Church',
            then: Joi.string().max(200).required(),
        })
        .when('addressType', {
            is: 'Funeral home',
            then: Joi.string().max(200).required(),
        })
        .when('addressType', {
            is: 'Residence',
            then: Joi.optional().allow(''),
        })
        .when('addressType', {
            is: 'Apartment',
            then: Joi.optional().allow(''),
        })
        .when('addressType', {
            is: 'APO/FPO',
            then: Joi.optional().allow(''),
        })
        .when('addressType', {
            is: 'P.O.Box',
            then: Joi.optional().allow(''),
        })
        .when('addressType', {
            is: 'APO/FPO',
            then: Joi.optional().allow(''),
        })
        .messages({
            'string.empty': 'This field is required.',
        })
        .label('employerName'),
    addressLineOne: Joi.string()
        .custom((value, helpers) => {
            const [{ addressLineOne, city, state, zip, addressType }] = helpers.state.ancestors
            if ((!addressLineOne && (addressLineOne || state || zip || city))) {

                if (addressType === 'APO/FPO') {
                    return helpers.error('string.required', {
                        custom: {
                            message: 'Unit/Ship/Psc Number is required.',
                        },
                    });
                }
                if (addressType === 'P.O.Box') {
                    return helpers.error('string.required', {
                        custom: {
                            message: 'P.O.Box is required.',
                        },
                    });
                }

                return helpers.error('string.required', {
                    custom: {
                        message: 'Address is required.',
                    },
                });
            }

            return value;
        })
        .optional()
        .min(0)
        .max(100)
        .messages({
            'string.required': '{{#custom.message}}',
        })
        .label('address'),
    addressLineTwo: Joi.string()
        .custom((value, helpers) => {
            const [{ addressLineOne, addressLineTwo, city, state, zip, addressType }] = helpers.state.ancestors

            if (addressLineTwo?.length > 30) {
                return helpers.error('string.max', {
                    custom: {
                        message: 'This field cannot exceed 30 characters.',
                    },
                });
            }

            if ((!addressLineTwo && (addressLineOne || state || zip || city))) {

                if (addressType === 'APO/FPO') {
                    return helpers.error('string.required', {
                        custom: {
                            message: 'Box/Hull Number is required.',
                        },
                    });
                }
            }
            return value;
        })
        .optional()
        .min(0)
        .max(30)
        .messages({
            'string.required': '{{#custom.message}}',
            'string.max': '{{#custom.message}}',
        })
        .label('Address - Apt #, Suite, Floor, Etc.'),
    city: Joi.string()
        .custom((value, helpers) => {
            const [{ addressLineOne, city, state, zip, country }] = helpers.state.ancestors;
            if (city && !city.match(/^[a-zA-Z]+(?:[\s-][a-zA-Z]+)*$/) && country !== 'Canada') {
                return helpers.error('string.pattern.base', {
                    custom: {
                        message: 'Please enter a valid city name.',
                    },
                });
            }

            if ((!city && (addressLineOne || state || zip))) {
                return helpers.error('string.required', {
                    custom: {
                        message: 'City is required.',
                    },
                });
            }

            return value;
        })
        .min(0)
        .max(25)
        .optional()
        .messages({
            'string.required': '{{#custom.message}}',
            'string.pattern.base': 'Please enter a valid city name.',
        })
        .label('City'),
    state: Joi.string()
        .custom((value, helpers) => {
            const [{ addressLineOne, city, state, zip, country }] = helpers.state.ancestors
            if ((!state && (addressLineOne || city || zip))) {
                if (country === 'CA' || country === 'CAN' || country === 'Canada' || country === 'CN') {
                    return helpers.error('string.required', {
                        custom: {
                            message: 'Province is required.',
                        },
                    });
                }
                return helpers.error('string.required', {
                    custom: {
                        message: 'State is required.',
                    },
                });
            }

            return value;
        })
        .optional()
        .min(0)
        .messages({
            'string.required': '{{#custom.message}}',
        })
        .label('State'),
    zip: Joi.string()
        .custom((value, helpers) => {
            const [{ addressLineOne, city, state, zip, country }] = helpers.state.ancestors
            if ((!zip && (addressLineOne || city || state))) {
                return helpers.error('string.required', {
                    custom: {
                        message: 'Zip Code is required.',
                    },
                });
            }

            if (country === 'Canada') {
                if (!zip.match(/^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/)) {
                    return helpers.error('string.pattern.base', {
                        custom: {
                            message: 'Invalid Zip Code',
                        }
                    });
                }
            }
            else {
                if (zip.length !== 0 && (zip.length < 5 || (zip.length > 5 && zip.replace("-", "").trim().length !== 9))) {
                    return helpers.error('string.pattern.base', {
                        custom: {
                            message: 'Invalid Zip Code',
                        }
                    });
                }
            }


            return value;
        })
        .optional()
        .min(0)
        .max(10)
        // .pattern(/(^\d{5}$)|(^\d{5}-\d{4}$)|(^[A-Z|a-z]\d[A-Z|a-z] \d[A-Z|a-z]\d$)/)
        .messages({
            'string.required': 'Invalid Zip Code',
            'string.pattern.base': 'Invalid Zip Code',
            'string.max': 'Invalid Zip Code',
        })
        .label('Zip Code'),
    country: Joi.string()
        .custom((value, helpers) => {
            const [{ addressLineOne, city, state, zip, country }] = helpers.state.ancestors
            if ((!country && (addressLineOne || city || state || zip))) {
                return helpers.error('string.required', {
                    custom: {
                        message: 'Country is required.',
                    },
                });
            }

            return value;
        })
        .optional()
        .min(0)
        .messages({
            'string.required': '{{#custom.message}}',
        })
        .label('Country'),
    defaultKey: Joi.boolean()
        .optional()
        .label('Default address check'),
    suggestedAddress: Joi.string()
        .allow('')
        .optional()
        .label('Suggested Address'),
    rcpEmail: Joi.string()
        .trim()
        .optional()
        .allow("")
        .label('E-mail Address')
        .custom((value, helpers) => {
            const [{ rcpEmail }] = helpers.state.ancestors
            if (rcpEmail && !validateEmailAddress(rcpEmail)) {
                return helpers.error('string.pattern.base', {
                    custom: {
                        message: 'Invalid email address.',
                    },
                });
            }

            return value;
        })
        .messages({
            'string.pattern.base': '{{#custom.message}}',
        }),
    rcpEmailType: Joi.string().valid(emailTypes[0]?.toLowerCase(), emailTypes[1]?.toLowerCase()),
    rcpPhone: Joi.string()
        .trim()
        .min(14)
        .max(15)
        .allow('')
        .custom((value, helpers) => {
            if (!value.match(/^\(?([1-9]\d{2})\)? \d{3}-\d{4}$/)) {
                return helpers.error('string.pattern.base', {
                    custom: {
                        message: `Please enter a valid Phone number.`,
                    },
                });
            }
            if (value.length < 14 || value.length > 15) {
                return helpers.error('string.length', {
                    custom: {
                        message: `Phone number must be 10 digits long.`,
                    },
                });
            }
            return value;
        })
        .messages({
            'string.pattern.base': '{{#custom.message}}',
            'string.length': '{{#custom.message}}',
        })
        .label('Phone Number'),
    rcpPhoneType: Joi.string().valid(phoneNumberTypes[0]?.toLowerCase(), phoneNumberTypes[1]?.toLowerCase()),
});

export const schemaForEditProfile = Joi.object({
    rcpFirstName: Joi.string()
        .trim()
        .min(1)
        .max(14)
        .required()
        .pattern(/^[a-zA-Z ]+$/)
        .messages({
            'string.empty': 'First Name is required.',
            'string.pattern.base': 'Please use only alphabetic characters.',
        })
        .label('First Name'),

    rcpLastName: Joi.string()
        .trim()
        .min(1)
        .max(14)
        .required()
        .pattern(/^[a-zA-Z ]+$/)
        .messages({
            'string.empty': 'Last Name is required.',
            'string.pattern.base': 'Please use only alphabetic characters.',
        })
        .label('Last Name'),
    rcpEmployerName: Joi.string()
        .min(0)
        .max(200)
        .optional()
        .label('Company Name'),
    relationship: Joi.any().optional().label('Relationship'),
    rcpEmail: Joi.string()
        .trim()
        .optional()
        .allow("")
        .label('E-mail Address')
        .custom((value, helpers) => {
            const [{ rcpEmail }] = helpers.state.ancestors
            if (rcpEmail && !validateEmailAddress(rcpEmail)) {
                return helpers.error('string.pattern.base', {
                    custom: {
                        message: 'Invalid email address.',
                    },
                });
            }

            return value;
        })
        .messages({
            'string.pattern.base': '{{#custom.message}}',
        }),
    rcpEmailType: Joi.string().valid(emailTypes[0]?.toLowerCase(), emailTypes[1]?.toLowerCase()),
    rcpPhone: Joi.string()
        .trim()
        .min(14)
        .max(15)
        .allow('')
        .custom((value, helpers) => {
            if (!value.match(/^\(?([1-9]\d{2})\)? \d{3}-\d{4}$/)) {
                return helpers.error('string.pattern.base', {
                    custom: {
                        message: `Please enter a valid Phone number.`,
                    },
                });
            }
            if (value.length < 14 || value.length > 15) {
                return helpers.error('string.length', {
                    custom: {
                        message: `Phone number must be 10 digits long.`,
                    },
                });
            }
            return value;
        })
        .messages({
            'string.pattern.base': '{{#custom.message}}',
            'string.length': '{{#custom.message}}',
        })
        .label('Phone Number'),
    rcpPhoneType: Joi.string().valid(phoneNumberTypes[0]?.toLowerCase(), phoneNumberTypes[1]?.toLowerCase()),
});

export const schemaForEditOccasion = Joi.object({
    occasions: Joi.array().items(
        Joi.object().keys({
            dateMM: Joi.string().allow(''),
            dateDD: Joi.string().allow(''),
            dateYYYY: Joi.any().optional(),
            addressBookEntryId: Joi.any().optional(),
            desc: Joi.any().optional(),
            lastUpdateDate: Joi.any().optional(),
            occasion: Joi.any().optional(),
            occasionId: Joi.any().optional(),
            rcpId: Joi.any().optional(),
            createdUser: Joi.any().optional(),
            createdDate: Joi.any().optional(),
            status: Joi.any().optional(),
            contactId: Joi.any().optional(),
            subscribed: Joi.any().optional(),
            subscribedEmail: Joi.boolean().optional(),
            subscribedPhone: Joi.boolean().optional(),
            subscribedPush: Joi.boolean().optional(),
            reminderEmail: Joi.any().optional(),
            updateDate: Joi.any().optional(),
            updateUser: Joi.any().optional(),
            reminderPhone: Joi.string()
                .trim()
                .when('subscribedPhone', {
                    is: true,
                    then: Joi.string()
                        .trim()
                        .pattern(/^\(?([1-9]\d{2})\)? \d{3}-\d{4}$/)
                        .min(14)
                        .max(15)
                        .required()
                        .messages({
                            'string.empty': 'Mobile number is required.',
                            'string.min': 'Mobile number must be 10 digits long.',
                            'string.max': 'Mobile number must be 10 digits long.',
                            'string.pattern.base': 'Please enter a valid mobile number.',
                        })
                        .label('Mobile number'),
                    otherwise: Joi.string().allow(''),
                }
                ),
            isOneDay: Joi.any().optional(),
            isThreeDays: Joi.any().optional(),
            isOneWeek: Joi.any().optional(),
            isTwoWeek: Joi.any().optional(),
            isOneMonth: Joi.any().optional(),
            isSnoozed: Joi.any().optional(),
            brandId: Joi.any().optional(),
            custFirstName: Joi.any().optional(),
            custLastName: Joi.any().optional(),
        })
            .custom((value, helpers) => {
                if ((Number(value.occasion) < 5 && Number(value.occasion) !== 2 && !value.dateMM && value.dateDD)) {
                    return helpers.error('any.required', {
                        custom: {
                            message: 'Month is required.',
                        },
                    });
                }
                if ((Number(value.occasion) < 5 && Number(value.occasion) !== 2 && value.dateMM && !value.dateDD)) {
                    return helpers.error('any.required', {
                        custom: {
                            message: 'Day is required',
                        },
                    });
                }

                if (Number(value.occasion) < 5 && Number(value.occasion) !== 2 && !value.dateMM) {
                    return helpers.error('any.required', {
                        custom: {
                            message: 'Month is required',
                        },
                    });
                }

                if (Number(value.occasion) < 5 && Number(value.occasion) !== 2 && !value.dateDD) {
                    return helpers.error('any.required', {
                        custom: {
                            message: 'Day is required',
                        },
                    });
                }

                return value;
            }),
    ).messages({
        'any.required': '{{#custom.message}}',
    }),
})

export const schemaForMailingAddress = Joi.object({
    addressType: Joi.string()
        .required()
        .messages({
            'string.empty': 'Location Type is required.',
        }),
    employerName: Joi.string()
        .when('addressType', {
            is: 'Business',
            then: Joi.string().max(200).required(),
        })
        .when('addressType', {
            is: 'Hospital',
            then: Joi.string().max(200).required(),
        })
        .when('addressType', {
            is: 'School',
            then: Joi.string().max(200).required(),
        })
        .when('addressType', {
            is: 'Church',
            then: Joi.string().max(200).required(),
        })
        .when('addressType', {
            is: 'Funeral home',
            then: Joi.string().max(200).required(),
        })
        .when('addressType', {
            is: 'Residence',
            then: Joi.optional().allow(''),
        })
        .when('addressType', {
            is: 'Apartment',
            then: Joi.optional().allow(''),
        })
        .when('addressType', {
            is: 'APO/FPO',
            then: Joi.optional().allow(''),
        })
        .when('addressType', {
            is: 'P.O.Box',
            then: Joi.optional().allow(''),
        })
        .when('addressType', {
            is: 'APO/FPO',
            then: Joi.optional().allow(''),
        })
        .messages({
            'string.empty': 'This field is required.',
        })
        .label('employerName'),
    addressLineOne: Joi.string()
        .custom((value, helpers) => {
            const [{ addressLineOne, city, state, zip, addressType }] = helpers.state.ancestors
            if ((!addressLineOne && (addressLineOne || state || zip || city))) {

                if (addressType === 'APO/FPO') {
                    return helpers.error('string.required', {
                        custom: {
                            message: 'Unit/Ship/Psc/Box Number is required.',
                        },
                    });
                }
                if (addressType === 'P.O.Box') {
                    return helpers.error('string.required', {
                        custom: {
                            message: 'P.O.Box is required.',
                        },
                    });
                }

                return helpers.error('string.required', {
                    custom: {
                        message: 'Address is required.',
                    },
                });
            }

            return value;
        })
        .optional()
        .min(0)
        .max(100)
        .messages({
            'string.required': '{{#custom.message}}',
        })
        .label('address'),
    addressLineTwo: Joi.string()
        .custom((value, helpers) => {
            const [{ addressLineOne, addressLineTwo, city, state, zip, addressType }] = helpers.state.ancestors

            if (addressLineTwo?.length > 30) {
                    return helpers.error('string.max', {
                        custom: {
                            message: 'This field cannot exceed 30 characters.',
                        },
                    });
            }

            if ((!addressLineTwo && (addressLineOne || state || zip || city))) {

                if (addressType === 'APO/FPO') {
                    return helpers.error('string.required', {
                        custom: {
                            message: 'Box/Hull Number is required.',
                        },
                    });
                }
            }
            return value;
        })
        .optional()
        .min(0)
        .max(30)
        .messages({
            'string.required': '{{#custom.message}}',
            'string.max': '{{#custom.message}}',
        })
        .label('Address - Apt #, Suite, Floor, Etc.'),
    city: Joi.string()
        .custom((value, helpers) => {
            const [{ city, country }] = helpers.state.ancestors;
            if (city && !city.match(/^[a-zA-Z]+(?:[\s-][a-zA-Z]+)*$/) && country !== 'Canada') {
                return helpers.error('string.pattern.base', {
                    custom: {
                        message: 'Please enter a valid city name.',
                    },
                });
            }

            if (!city) {
                return helpers.error('string.required', {
                    custom: {
                        message: 'City is required.',
                    },
                });
            }

            return value;
        })
        .min(0)
        .max(25)
        .optional()
        .messages({
            'string.required': '{{#custom.message}}',
            'string.pattern.base': 'Please enter a valid city name.',
        })
        .label('City'),
    state: Joi.string()
        .custom((value, helpers) => {
            const [{ addressLineOne, city, state, zip, country }] = helpers.state.ancestors
            if ((!state && (addressLineOne || city || zip || country))) {
                if (country === 'CA' || country === 'CAN' || country === 'Canada' || country === 'CN') {
                    return helpers.error('string.required', {
                        custom: {
                            message: 'Province is required.',
                        },
                    });
                }
                return helpers.error('string.required', {
                    custom: {
                        message: 'State is required.',
                    },
                });
            }

            return value;
        })
        .optional()
        .min(0)
        .messages({
            'string.required': '{{#custom.message}}',
        })
        .label('State'),
    zip: Joi.string()
        .custom((value, helpers) => {
            const [{ zip, country, addressLineOne, city, state }] = helpers.state.ancestors
            if ((!zip && (addressLineOne || city || state))) {
                return helpers.error('string.required', {
                    custom: {
                        message: 'Zip Code is required.',
                    },
                });
            }
            if (zip.length > 0) {
                if (country === 'Canada') {
                    if (!zip.match(/^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/)) {
                        return helpers.error('string.pattern.base', {
                            custom: {
                                message: 'Invalid Zip Code',
                            }
                        });
                    }
                }

                if (country === 'United States') {
                    if (zip.length !== 5 && !zip.match(/^\d{5}(?:-\d{4})?$/)) {
                        return helpers.error('string.pattern.base', {
                            custom: {
                                message: 'Invalid Zip Code',
                            }
                        });
                    }
                }

            }

            return value;
        })
        .optional()
        .min(0)
        .max(10)
        .messages({
            'string.required': 'Zip Code is required',
            'string.pattern.base': 'Invalid Zip Code',
            'string.max': 'Invalid Zip Code',
        })
        .label('Zip Code'),
    country: Joi.string()
        .required()
        .messages({
            'string.empty': 'Country is required.',
        })
        .label('Country'),
    defaultKey: Joi.boolean()
        .optional()
        .label('Default address check'),
    suggestedAddress: Joi.string()
        .allow('')
        .optional()
        .label('Suggested Address'),
})

export default {};
